import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, IconButton } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useNavigate } from 'react-router-dom';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { URL } from '../../../common/api/urls';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CountdownTimer from './countdown_timer';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '500px' },
  maxWidth: '600px',
  bgcolor: '#ffffff',
  borderRadius: '20px',
  padding: '30px',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
  animation: 'slideIn 0.5s ease',
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translate(-50%, -60%) scale(0.9)'
    },
    to: {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1)'
    }
  }
};

/**
 * NotificationPopup - Backend-driven notification popup
 * 
 * Props:
 * - open: boolean - controls popup visibility
 * - onClose: function - callback when popup is closed
 * - notifData: object - notification data from backend with structure:
 *   {
 *     title: string - popup title
 *     msg: string - notification message
 *     img: string - image URL (can be empty)
 *     action: string - navigation path (e.g., "/subscription")
 *     type: string - "action" | "info" | "auto-close" (determines button behavior)
 *     autoCloseSeconds: number - optional, for auto-close type only
 *   }
 */
const NotificationPopup = ({ open, onClose, notifData }) => {
  const navigate = useNavigate();
  const [localOpen, setLocalOpen] = useState(open);
  const [cancel, responseData, error, loaded, reset, executeAPI] = useAxiosWithAuth();

  useEffect(() => {
    setLocalOpen(open);
  }, [open]);

  // Auto-close timer for "auto-close" type
  useEffect(() => {
    if (open && notifData?.type === 'auto-close' && notifData?.autoCloseSeconds) {
      const timer = setTimeout(() => {
        handleClose();
      }, notifData.autoCloseSeconds * 1000);

      return () => clearTimeout(timer);
    }
  }, [open, notifData]);

  const handleClose = () => {
    setLocalOpen(false);
    if (onClose) onClose();
  };

  const handleAction = () => {
    if (notifData?.action) {
      handleClose();
      // Navigate to the action path
      navigate(notifData.action);
    } else {
      // If no action, just close
      handleClose();
    }
  };

  if (!notifData) return null;

  const { title, timer_end, msg, img, action, type } = notifData;
  const hasAction = action && action !== "";
  const isActionType = type === "action";
  const isInfoType = type === "info";
  const isAutoCloseType = type === "auto-close";

  // Build full image path backend sends only filename
  const getImageUrl = (filename) => {
    if (!filename || filename === "") return "";
    // If already a full URL (http/https), return as is
    if (filename.startsWith('http://') || filename.startsWith('https://')) {
      return filename;
    }
    // If already starts with /, return as is
    if (filename.startsWith('/')) {
      return filename;
    }
    // Otherwise prepend /assets/images/
    return `/assets/images/${filename}`;
  };

  const imageUrl = getImageUrl(img);

  return (
    <Modal
      open={localOpen}
      onClose={handleClose}
      aria-labelledby="notification-popup-title"
      aria-describedby="notification-popup-description"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(30, 60, 114, 0.6)',
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.4s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            }
          }
        }
      }}
    >
      <Box sx={modalStyle}>
        {/* Image Section */}
        {timer_end?
        <CountdownTimer targetDate={timer_end} customColor="red" />
        :<></>}
        {imageUrl && imageUrl !== "" && (
          <Box
            sx={{
              width: '100%',
              mb: 3,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          >
            <img
              src={imageUrl}
              alt={title || "Notification"}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
              onError={(e) => {
                // Hide image if loading fails
                e.target.style.display = 'none';
              }}
            />
          </Box>
        )}

        {/* Title */}
        {title && (
          <Typography
            id="notification-popup-title"
            variant="h4"
            component="h2"
            sx={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#333333',
              marginBottom: '15px',
              textAlign: 'center',
              lineHeight: '1.2'
            }}
          >
            {title}
          </Typography>
        )}

        {/* Message */}
        {msg && (
          <Box
            id="notification-popup-description"
            sx={{
              color: '#555555',
              lineHeight: '1.6',
              marginBottom: '20px',
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'left',
              '& p': {
                margin: '0 0 12px 0'
              },
              '& p:last-child': {
                marginBottom: 0
              },
              '& h1, & h2, & h3, & h4': {
                color: '#333333',
                lineHeight: 1.3,
                margin: '0 0 10px 0'
              },
              '& h1': { fontSize: '22px' },
              '& h2': { fontSize: '20px' },
              '& h3': { fontSize: '18px' },
              '& h4': { fontSize: '16px' },
              '& ul, & ol': {
                margin: '0 0 12px 18px',
                padding: 0
              },
              '& li': {
                marginBottom: '4px'
              },
              '& blockquote': {
                margin: '0 0 12px 0',
                padding: '10px 14px',
                borderLeft: '4px solid #667eea',
                backgroundColor: '#f6f8ff',
                borderRadius: '6px'
              },
              '& a': {
                color: '#3f51b5',
                textDecoration: 'underline'
              },
              '& strong': {
                fontWeight: 700
              },
              '& em': {
                fontStyle: 'italic'
              },
              '& code': {
                fontFamily: 'monospace',
                fontWeight: 600,
                backgroundColor: '#f4f4f4',
                borderRadius: '4px',
                padding: '0 4px'
              }
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children, ...props }) => {
                  const safeHref = href || '';
                  const isExternal = /^https?:\/\//i.test(safeHref);
                  return (
                    <a
                      {...props}
                      href={safeHref}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {msg}
            </ReactMarkdown>
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            marginTop: '30px',
            display: 'flex',
            gap: '15px',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center'
          }}
        >
          {/* Primary Action Button - Only for "action" type */}
          {isActionType && hasAction && (
            <Button
              variant="contained"
              onClick={handleAction}
              sx={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: '#ffffff',
                padding: '12px 25px',
                border: 'none',
                borderRadius: '25px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flex: 1,
                fontSize: '14px',
                textTransform: 'none',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                  background: 'linear-gradient(135deg, #5a6fd8, #6a4190)'
                }
              }}
            >
              Ok
            </Button>
          )}

          {/* Close Button*/}
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              background: 'transparent',
              color: '#667eea',
              border: '2px solid #667eea',
              padding: '12px 25px',
              borderRadius: '25px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flex: 1,
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                background: '#667eea',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NotificationPopup;
